<template>
    <div :id="`form_${inputId}`" class="mtw-form-group w-100 animate__animated" :class="{ selectDisabled: disabled }"
        role="group" :aria-labelledby="label ? `${inputId}-label` : null" :aria-disabled="String(!!disabled)"
        style="z-index: 1;">
        <div class="d-flex w-100 justify-content-between align-items-start group-header">
            <label v-if="label && label.length > 0" :id="`${inputId}-label`" :for="inputId" class="group-label">
                <span v-if="infoText && infoText.length > 0" class="infoIcon" :title="infoText" aria-label="Info">
                    <!-- Inline info icon (no BootstrapVue icons) -->
                    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                        <path fill="currentColor" d="M11 9h2V7h-2v2zm1-7C6.48 2 2 6.48 2 12s4.48 10 10 10
                   10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6z" />
                    </svg>
                </span>
                {{ $i18n.t(label) }}
                <span v-if="required" class="required" title="required">*</span>
            </label>

            <div v-if="hasDetails && value" ref="detailsButton" class="detailsButton" @click="ondblclick"
                :title="$i18n.t('details')">
                <!-- Outline info icon -->
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                    <path fill="currentColor" d="M11 7h2v2h-2V7zm-1 4h2v6h-2v-6zm2-9C6.48 2 2 6.48 2 12s4.48 10
                 10 10 10-4.48 10-10S17.52 2 12 2z" />
                </svg>
                <p @dblclick.stop="ondblclick">{{ $i18n.t('details') }}</p>
            </div>

            <div v-if="toggleItems" class="toggleItemsButton">
                <p class="mb-0" @click="toggleItemsAction">
                    <span class="toggleIcon" aria-hidden="true">
                        <svg v-if="isToggled" viewBox="0 0 24 24" width="16" height="16">
                            <!-- eye-slash -->
                            <path fill="currentColor"
                                d="M12 6c3.7 0 6.9 2 8.5 5-0.34.62-.77 1.2-1.25 1.74l1.42 1.42A12.1 12.1 0 0 0 24 11c-2.2-4-6.5-7-12-7-1.81 0-3.53.36-5.09 1L8 6.09C9.26 5.39 10.6 5 12 5v1zM1.39 4.22 3.17 6A12.1 12.1 0 0 0 0 11c2.2 4 6.5 7 12 7 2.07 0 4.03-.43 5.8-1.2l1.98 1.98 1.41-1.41L2.81 2.81 1.39 4.22zM12 17c-3.7 0-6.9-2-8.5-5 .73-1.35 1.83-2.5 3.18-3.36l2.04 2.04A4 4 0 0 0 12 16a3.98 3.98 0 0 0 2.31-.75l1.57 1.57c-1.06.38-2.2.58-3.38.58zm0-2a2 2 0 0 1-2-2c0-.36.1-.7.27-1l2.73 2.73c-.3.17-.64.27-1 .27z" />
                        </svg>
                        <svg v-else viewBox="0 0 24 24" width="16" height="16">
                            <!-- eye -->
                            <path fill="currentColor"
                                d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5C21.27 7.61 17 4.5 12 4.5zm0 12a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9zm0-7a2.5 2.5 0 1 0 .001 5.001A2.5 2.5 0 0 0 12 9.5z" />
                        </svg>
                    </span>
                    {{ toggleText }}
                </p>

                <div class="d-flex justify-content-center align-items-center">
                    <button class="btn btn-info btn-sm d-flex align-items-center justify-content-center" type="button"
                        style="color: white !important; width: 32px; height: 32px; border-radius: 50%; padding: 0; background-color: transparent; border: 0px;"
                        :title="$i18n.t('clear')" @click="$emit('refresh')">
                        <i class="material-icons">refresh</i>
                    </button>
                </div>

            </div>
        </div>

        <vue-select append-to-body :disabled="disabled" v-model="value" :id="inputId" :get-option-label="optionLabel"
            :multiple="multiple" :options="computedOptions" @input="emitChanges" @search="changeSearch"
            :label="optionText" class="mtw-select">
            <template #option="option">
                <div class="custom-option">
                    {{
                        $i18n.te(getText(option))
                            ? $i18n.t(getText(option))
                            : getText(option)
                    }}
                </div>
            </template>

            <template #selected-option="{ }">
                <div class="custom-option">
                    {{
                        $i18n.te(getText(value))
                            ? $i18n.t(getText(value))
                            : getText(value)
                    }}
                </div>
            </template>

            <template #no-options="{ search, searching }">
                <template v-if="searching">
                    <div class="w-100" @dblclick="ondblclick">
                        {{ $i18n.t('notFoundInSelect') }} <em>"{{ search }}"</em>.
                    </div>
                </template>
            </template>

            <template #search="{ attributes, events }">
                <input :id="`select-${inputId}`" class="vs__search select" :required="!value" v-bind="attributes"
                    v-on="events" @keydown="handleKeydown" />
            </template>
        </vue-select>


        <p v-if="!isValid" class="invalidMessage">{{ invalidMessage }}</p>

        <transition name="fade" mode="out-in">
            <div v-if="component !== '' && showComponent" class="w-100 detailsContainer component">
                <page-header :name="$i18n.t(name)" :button="false" small hideSearch />
                <component :is="component" :id="value && value.id" :key="componentKey" @cancel="cancelHandler"
                    @change="changeHandler" @addElement="addElement" @updateElement="updateElement" />
            </div>
        </transition>
    </div>
</template>

<script>
/* eslint-disable */
import 'vue-select/dist/vue-select.css';

export default {
    data() {
        return {
            value: '',
            searchText: '',
            componentKey: 0,
            showComponent: false,
            isToggled: false,
        };
    },
    props: {
        options: { type: Array, required: true },
        optionText: { type: String, required: false },
        label: { type: String, required: false, default: 'text' },
        name: { type: String, required: true },
        invalidMessage: { type: String, required: false, default: 'Invalid' },
        isValid: { type: Boolean, required: false, default: true },
        required: { type: Boolean, required: false, default: false },
        multiple: { type: Boolean, required: false, default: false },
        selected: { required: false, default: undefined },
        component: { type: String, required: false, default: '' },
        hasDetails: { type: Boolean, required: false, default: false },
        disabled: { type: Boolean, required: false, default: false },
        toggleItems: { type: Boolean, required: false, default: false },
        toggleState: { type: Boolean, required: false, default: false },
        toggleLabels: { type: Array, required: false },
        infoText: { type: String, required: false, default: '' },
        translate: { type: Boolean, required: false, default: false },
        width: { type: String, default: '200px' },
    },
    computed: {
        inputId() {
            return this.name + 'Input';
        },
        toggleIcon() {
            return this.isToggled ? 'eye-slash-fill' : 'eye-fill';
        },
        toggleText() {
            return this.isToggled ? this.toggleLabels?.[0] : this.toggleLabels?.[1];
        },
        computedOptions() {
            const options = this.options || [];
            const optionText = this.optionText;
            const searchText = (this.searchText || '').trim().toLowerCase();
            if (searchText.length === 0) return options;

            return options.filter((item) => {
                const raw = item && optionText ? item[optionText] : '';
                const textValue = this.translate && this.$i18n ? this.$i18n.t(raw) : raw;
                return String(textValue || '').toLowerCase().includes(searchText);
            });
        },
    },
    methods: {
        isObject(value) {
            return typeof value === 'object' && value !== null;
        },
        getText(value) {
            if (this.isObject(value)) {
                return value[this.optionText] !== undefined ? value[this.optionText] : '';
            } else {
                if (value === undefined) return '';
                if (this.optionText === undefined) return value;
                return (this.computedOptions[value] || {})[this.optionText] || '';
            }
        },
        updateDisplay() {
            const dropdown = this.$el.querySelector('.vs__selected-options');
            const select = this.$el.querySelector('.vs__selected');
            const input = this.$el.querySelector('.vs__search');
            if (dropdown && select) {
                const height = select.offsetHeight;
                const minHeight = input ? input.offsetHeight : 28;
                dropdown.style.minHeight = `${minHeight}px`;
                dropdown.style.height = `${height}px`;
            }
        },
        handleKeydown(e) {
            if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        },
        updateElement(element) {
            const index = this.options.map((item) => item.id).indexOf(element.value.id);
            this.$set(this.options, index, element.value);
            this.closeItems();
        },
        optionLabel(label) {
            const optionText = this.optionText;
            const raw = label ? label[optionText] : '';
            return this.translate && this.$i18n ? this.$i18n.t(raw) : raw;
        },
        changeSearch(text) {
            this.searchText = text;
        },
        ondblclick() {
            if (this.$refs.detailsButton) {
                this.showComponent = !this.showComponent;
                this.$refs.detailsButton.classList.toggle('open');
            }
        },
        toggleItemsAction() {
            this.isToggled = !this.isToggled;
            this.$emit('toggle', this.isToggled);
        },
        closeItems() {
            this.showComponent = false;
            if (this.$refs.detailsButton) {
                this.$refs.detailsButton.classList.toggle('open');
            }
        },
        emitChanges(e) {
            if (e && e.id && e.id === 0) {
                this.showComponent = true;
                this.$refs.detailsButton && this.$refs.detailsButton.classList.toggle('open');
            } else {
                this.componentKey += 1;
                this.$emit('change', this.value);
            }
            this.$nextTick(this.updateDisplay);
        },
        changeHandler(e) {
            this.$emit('change', e);
        },
        cancelHandler() {
            if (this.$refs.detailsButton) {
                this.showComponent = false;
                this.$refs.detailsButton.classList.remove('open');
            }
        },
        addElement(newElement) {
            this.$set(this.options, this.options.length - 1, newElement.value);
            this.options.push(newElement.element);
            this.componentKey += 1;
            this.showComponent = false;
            this.$refs.detailsButton && this.$refs.detailsButton.classList.remove('open');
        },
    },
    mounted() {
        const hasSelected =
            this.selected !== undefined &&
            this.selected !== null &&
            this.selected !== '';

        if (hasSelected) {
            this.value = this.selected;
        } else if (this.options[0]) {
            this.value = this.options[0];
            this.$emit('change', this.value);
        }

        this.isToggled = this.toggleState;

        this.$nextTick(this.updateDisplay);
    },
    watch: {
        isValid() {
            this.$el.classList.remove('animate__headShake');
            if (!this.isValid) {
                this.$el.classList.add('animate__headShake');
            }
        },
        selected: {
            deep: true,
            handler() {
                if (this.selected)
                    this.value = this.selected;
            }
        },
    },
};
</script>

<style scoped>
.mtw-select {
    height: auto;
    min-height: 33.33px;

}

.custom-option {
    display: inline-block;
    white-space: nowrap;
    overflow: inherit;
    text-overflow: ellipsis;

}

.infoIcon {
    margin-right: 0.25rem;
    color: #e0900f;
    display: inline-flex;
    vertical-align: middle;
}

.selectDisabled {
    opacity: 0.75;
}

.invalidMessage {
    color: #c43845;
    margin: 0;
    font-style: italic;
    font-weight: bolder;
    background: #b92e2e2b;
    opacity: 1;
    text-align: center;
}

.fade-enter-active,
.fade-leave-active {
    transition: all .5s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.component {
    border: 0.1px dashed rgba(217, 217, 217, 0.89);
    border-radius: 8px;
    padding: 1rem;
    background: #1a2534;
}

.group-label {
    text-align: left;
    color: rgb(var(--tmHubTheme-text-contrast));
    opacity: 0.75;
}

.required {
    color: rgba(255, 96, 96);
}

.suggestionSpan {
    background-color: rgb(215, 221, 223) !important;
    width: 100% !important;
    height: 100% !important;
}
</style>

<style lang="scss">
.vs__selected {
    margin: 2px 2px 0;
    padding: 0px;
}


.detailsButton {
    width: auto;
    cursor: pointer;
    background-color: transparent;
    border: 1.75px solid #e08f0e;
    padding: 0.15rem 0.5rem;
    border-radius: 10rem;
    opacity: 50%;
    margin-bottom: 0.25rem;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    justify-content: space-between;
    transition: opacity 0.25s ease-in-out, background-color 1s ease-in;

    &.open {
        background-color: #e08f0e;
        transition: background-color 1s ease-in;
        opacity: 1;
    }

    p {
        opacity: 1;
        margin: 0;
    }

    &:hover {
        transition: opacity 0.5s ease-in-out;
        opacity: 1;
    }
}

.detailsContainer {
    margin: 0.5rem 0;
}

.toggleItemsButton {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 10px;
    text-align: end;
    cursor: pointer;
}

.group-header {
    gap: .5rem;
}

* {
    --vs-dropdown-bg: #2a343e;
    --vs-dropdown-color: white;
    --vs-dropdown-option-color: white;
}


.mtw-select .vs__dropdown-menu {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
}


.v-select.vs--searchable {
    color: rgb(var(--tmHubTheme-text-contrast)) !important;

    >div {
        background-color: rgba(var(--tmHubTheme-primary-color), 0.05);
        padding: 0.65rem;
        border: 0.1px solid rgba(var(--tmHubTheme-text-contrast), 0.3);
        color: rgb(var(--tmHubTheme-text-contrast)) !important;
        border-radius: 8px;
    }

    .vs__selected {
        color: rgb(var(--tmHubTheme-text-contrast));
    }

    .vs__actions {
        .vs__clear {
            fill: #dc3545;
        }

        .vs__open-indicator {
            fill: rgb(var(--tmHubTheme-text-contrast));
        }
    }

    .vs__selected-options {
        max-width: 100%;
        text-overflow: ellipsis;
        white-space: nowrap;

        .vs__selected {
            white-space: nowrap;
            overflow: hidden;
            background: transparent !important;
            max-width: 100%;
            width: 100%;
            overflow: hidden;

            .vs__deselect {
                fill: #dc3545;
            }
        }
    }
}

.vs--disabled .vs__clear,
.vs--disabled .vs__dropdown-toggle,
.vs--disabled .vs__open-indicator,
.vs--disabled .vs__search,
.vs--disabled .vs__selected {
    background: transparent !important;
}

.select {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.vs__dropdown-menu {
    z-index: 9999999 !important;
    box-shadow: rgba(0, 0, 0, 0.4) 0px 30px 90px;
}
</style>